/**
 * Icons.
 *
 * One file, one visual language: 24×24, stroke only, `currentColor`, 1.75
 * weight, round caps. Anything that inherits colour works in the nav, on a
 * button, or inline in a row without adjustment.
 *
 * Cricket has no settled iconography, so the sport-specific ones are drawn from
 * things a scorer would recognise on sight rather than from a generic set: a
 * bat and ball for a match, a trophy for a tournament, a shield for a team.
 */

interface Props { size?: number; className?: string }

function Svg({ size = 22, className, children }: Props & { children: React.ReactNode }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth={1.75} strokeLinecap="round"
         strokeLinejoin="round" className={className} aria-hidden="true">
      {children}
    </svg>
  );
}

/* ── navigation ─────────────────────────────────────────────────────────── */

export function IconHome(p: Props) {
  return <Svg {...p}><path d="M3 10.5 12 3l9 7.5" /><path d="M5.5 9.5V20h13V9.5" />
    <path d="M9.5 20v-5.5h5V20" /></Svg>;
}

/** A bat and ball — the only thing that reads unambiguously as "a match". */
export function IconMatch(p: Props) {
  return <Svg {...p}>
    <path d="M14.5 3.5 20.5 9.5" /><path d="M13 5 5.5 12.5a2.1 2.1 0 0 0 0 3l3 3a2.1 2.1 0 0 0 3 0L19 11" />
    <circle cx="5" cy="19" r="2.4" />
  </Svg>;
}

export function IconPeople(p: Props) {
  return <Svg {...p}><circle cx="9" cy="8" r="3.2" />
    <path d="M3 20c0-3.3 2.7-5.4 6-5.4s6 2.1 6 5.4" />
    <path d="M16.5 5.6a3.2 3.2 0 0 1 0 6.1" /><path d="M18 14.9c2 .7 3.4 2.4 3.4 5.1" /></Svg>;
}

export function IconTeam(p: Props) {
  return <Svg {...p}><path d="M12 3 4.5 5.6v5.9c0 4.4 3.1 8.1 7.5 9.5 4.4-1.4 7.5-5.1 7.5-9.5V5.6z" />
    <path d="M9.3 12.2 11.3 14.3 15 10.4" /></Svg>;
}

export function IconTrophy(p: Props) {
  return <Svg {...p}><path d="M7.5 4h9v5a4.5 4.5 0 0 1-9 0z" />
    <path d="M7.5 5.5H5A2.2 2.2 0 0 0 5 10h2.6" /><path d="M16.5 5.5H19A2.2 2.2 0 0 1 19 10h-2.6" />
    <path d="M12 13.5V17" /><path d="M8.6 20.5h6.8" /><path d="M9.8 17h4.4l.6 3.5H9.2z" /></Svg>;
}

export function IconMusic(p: Props) {
  return <Svg {...p}><path d="M9 18V5l11-2v13" /><circle cx="6" cy="18" r="3" />
    <circle cx="17" cy="16" r="3" /></Svg>;
}

export function IconSettings(p: Props) {
  return <Svg {...p}><path d="M4 7h10" /><path d="M18 7h2" /><circle cx="16" cy="7" r="2" />
    <path d="M4 17h6" /><path d="M14 17h6" /><circle cx="12" cy="17" r="2" /></Svg>;
}

/* ── actions ────────────────────────────────────────────────────────────── */

export function IconPlus(p: Props) {
  return <Svg {...p}><path d="M12 5v14" /><path d="M5 12h14" /></Svg>;
}
export function IconTrash(p: Props) {
  return <Svg {...p}><path d="M4 7h16" /><path d="M9.5 7V4.8h5V7" />
    <path d="M6.5 7l.9 12.2A1.7 1.7 0 0 0 9.1 21h5.8a1.7 1.7 0 0 0 1.7-1.8L17.5 7" /></Svg>;
}
export function IconEdit(p: Props) {
  return <Svg {...p}><path d="M4 20h4.2L19 9.2a2.1 2.1 0 0 0-3-3L5 17z" /><path d="M14.5 6.5l3 3" /></Svg>;
}
export function IconChart(p: Props) {
  return <Svg {...p}><path d="M4 20V4" /><path d="M4 20h16" />
    <path d="M8 20v-6" /><path d="M13 20V9" /><path d="M18 20v-9.5" /></Svg>;
}
export function IconChevron({ size = 18, className }: Props) {
  return <Svg size={size} {...(className ? { className } : {})}><path d="M9 5l7 7-7 7" /></Svg>;
}
export function IconBack({ size = 22, className }: Props) {
  return <Svg size={size} {...(className ? { className } : {})}><path d="M15 5l-7 7 7 7" /></Svg>;
}
export function IconSearch(p: Props) {
  return <Svg {...p}><circle cx="11" cy="11" r="6.5" /><path d="M16 16l4.5 4.5" /></Svg>;
}
export function IconWatch(p: Props) {
  return <Svg {...p}><path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z" />
    <circle cx="12" cy="12" r="3" /></Svg>;
}
export function IconUndo(p: Props) {
  return <Svg {...p}><path d="M3 7v6h6" /><path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13" /></Svg>;
}
export function IconRedo(p: Props) {
  return <Svg {...p}><path d="M21 7v6h-6" /><path d="M3 17a9 9 0 0 1 9-9 9 9 0 0 1 6 2.3L21 13" /></Svg>;
}
export function IconTransfer(p: Props) {
  return <Svg {...p}><path d="M4 8h13" /><path d="M13.5 4.5 17 8l-3.5 3.5" />
    <path d="M20 16H7" /><path d="M10.5 12.5 7 16l3.5 3.5" /></Svg>;
}

/* ── status ─────────────────────────────────────────────────────────────── */

/** A live match. Pulses, because it is the one thing on a list that is moving. */
export function IconLive({ size = 8 }: { size?: number }) {
  return (
    <span className="relative inline-flex" style={{ width: size, height: size }}>
      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-leather opacity-70" />
      <span className="relative inline-flex h-full w-full rounded-full bg-leather" />
    </span>
  );
}

export function IconOffline(p: Props) {
  return <Svg {...p}><path d="M3 3l18 18" /><path d="M8.5 15.5a5 5 0 0 1 7 0" />
    <path d="M5 12a10 10 0 0 1 3.2-2.2" /><path d="M19 12a10 10 0 0 0-6.5-2.9" />
    <path d="M12 19h.01" /></Svg>;
}

export function IconCheck(p: Props) {
  return <Svg {...p}><path d="M5 12.5 9.5 17 19 7" /></Svg>;
}

export function IconMic(p: Props) {
  return <Svg {...p}><rect x="9" y="3" width="6" height="11" rx="3" />
    <path d="M5.5 11.5a6.5 6.5 0 0 0 13 0" /><path d="M12 18v3" /></Svg>;
}

export function IconLogout({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round"
         aria-hidden="true">
      <path d="M15 4h3a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-3" />
      <path d="M10 17l-5-5 5-5" />
      <path d="M5 12h11" />
    </svg>
  );
}
